import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import Alert from '@/shared/components/Alert';
import { fetchStatusMappings, saveStatusMappings } from '../statusMappingsApi';

// Laravel keys row errors as "mappings.<row>.<field>".
const rowErrors = (fieldErrors, index) => Object.entries(fieldErrors)
  .filter(([key]) => key.startsWith(`mappings.${index}.`))
  .flatMap(([, messages]) => messages);

// A row Admin added has no id yet; the API treats a row without one as new.
const toPayload = rows => rows.map(({ id, incoming_status, system_status }) => (
  id ? { id, incoming_status, system_status } : { incoming_status, system_status }
));

const EditableMappingRow = ({ row, systemStatuses, errors, onChange, onRemove }) => (
  <tr>
    <td>
      <input className='form-control' aria-label='Incoming status' value={row.incoming_status} onChange={onChange('incoming_status')} />
      {errors.map(text => <p key={text} className='text-danger mb-0'>{text}</p>)}
    </td>
    <td>
      <select className='form-control' aria-label='System status' value={row.system_status} onChange={onChange('system_status')}>
        {systemStatuses.map(status => <option key={status} value={status}>{status}</option>)}
      </select>
    </td>
    <td>
      <Button color='link' size='sm' className='mb-0' aria-label={`Remove ${row.incoming_status || 'new row'}`} onClick={onRemove}>
        Remove
      </Button>
    </td>
  </tr>
);

/**
 * Why: admins need to teach every participant import a client's status words without a
 *      deployment; a word nobody mapped fails its row in Import History.
 * Scope: the one global list. Active and Terminated are built in and shown as fixed rows; the
 *        statuses a row can point at are those built-ins, as the API sends them. Saves the whole
 *        list at once and then shows what the server saved (so new rows get their ids).
 * SRP: edits and saves the list; validation messages come from the API.
 */
const StatusMappingsEditor = () => {
  const [builtIn, setBuiltIn] = useState([]);
  const [rows, setRows] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isBusy, setBusy] = useState(false);

  const showSaved = saved => {
    setBuiltIn(saved.builtIn);
    setRows(saved.mappings);
  };

  useEffect(() => {
    fetchStatusMappings().then(loaded => {
      if (loaded.errors) {
        setError(loaded.errors);
        return;
      }
      showSaved(loaded);
    });
  }, []);

  const updateRow = (index, field) => event => setRows(rows.map((row, i) => (
    i === index ? { ...row, [field]: event.target.value } : row
  )));

  const systemStatuses = builtIn.map(row => row.system_status);

  const addRow = () => setRows([...rows, { incoming_status: '', system_status: systemStatuses[0] }]);

  const removeRow = index => () => setRows(rows.filter((_, i) => i !== index));

  const onSave = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    setFieldErrors({});
    const result = await saveStatusMappings(toPayload(rows));
    setBusy(false);
    if (result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
      return;
    }
    if (result.errors) {
      setError(result.errors);
      return;
    }
    showSaved(result);
    setMessage('Saved.');
  };

  return (
    <>
      <div className='card__title'>
        <h5 className='subhead'>
          Every participant import reads a file&apos;s status word through this list. A word that isn&apos;t
          Active, Terminated or listed here fails its row in Import History.
        </h5>
      </div>
      <table className='table table--bordered mb-3'>
        <thead><tr><th>Incoming status</th><th>System status</th><th /></tr></thead>
        <tbody>
          {builtIn.map(row => (
            <tr key={row.incoming_status} data-built-in>
              <td>{row.incoming_status}</td>
              <td>{row.system_status}</td>
              <td className='text-muted'>Built in</td>
            </tr>
          ))}
          {rows.map((row, index) => (
            <EditableMappingRow
              key={row.id ?? `new-${index}`}
              row={row}
              systemStatuses={systemStatuses}
              errors={rowErrors(fieldErrors, index)}
              onChange={field => updateRow(index, field)}
              onRemove={removeRow(index)}
            />
          ))}
        </tbody>
      </table>
      <Button color='secondary' size='sm' className='mb-3 mr-2' onClick={addRow}>Add mapping</Button>
      <Button color='primary' size='sm' className='mb-3' disabled={isBusy} onClick={onSave}>
        {isBusy ? <><Spinner size='sm' className='mr-2' />Saving...</> : 'Save'}
      </Button>
      {message && <Alert color='success' className='alert--bordered' icon><p>{message}</p></Alert>}
      {error && <Alert color='danger' className='alert--bordered' icon><p>{error}</p></Alert>}
    </>
  );
};

export default StatusMappingsEditor;
